import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { submitOfferRequest } from '../../services/cmsService';
import { checkOfferGenerationRights } from '../../services/creditsService';

const GenerateOfferButton = ({ auctionId, variant = 'button' }) => {
  const { isAuthenticated, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Funcții de formatare pentru response-uri
  const formatSuccessDetails = (result) => {
    const parts = [];

    if (result.requestId) {
      parts.push(`🎫 ID Cerere: ${result.requestId}`);
    }

    if (result.estimatedDelivery) {
      parts.push(`⏰ Livrare estimată: ${result.estimatedDelivery}`);
    } else {
      parts.push(`⏰ Veți fi contactat în cel mult 24 de ore`);
    }

    if (result.creditSource) {
      const creditSourceText =
        result.creditSource === 'Subscription'
          ? 'din abonament'
          : 'cumpărat extra';
      parts.push(`💳 Credit consumat: ${creditSourceText}`);
    }

    if (
      result.remainingAvailableCredits !== undefined &&
      result.remainingExtraCredits !== undefined
    ) {
      parts.push(
        `🔋 Credite rămase: ${result.remainingAvailableCredits} (abonament) + ${result.remainingExtraCredits} (extra) = ${result.totalRemainingCredits || parseInt(result.remainingAvailableCredits) + parseInt(result.remainingExtraCredits)} total`,
      );
    }

    if (result.usageId) {
      parts.push(`🔍 ID Utilizare: ${result.usageId}`);
    }

    if (result.processedAt) {
      parts.push(`📅 Procesat la: ${result.processedAt}`);
    }

    return parts.join('\n');
  };

  const formatErrorDetails = (result) => {
    const parts = [];

    if (result.errorCode) {
      parts.push(`❌ Cod eroare: ${result.errorCode}`);
    }

    if (result.errorCode === 'INSUFFICIENT_CREDITS') {
      parts.push(
        `💡 Soluție: Cumpărați credite extra sau actualizați-vă abonamentul`,
      );
      parts.push(`🛒 Link: /cumpara-credite`);
    }

    if (result.details) {
      parts.push(`📋 Detalii: ${result.details}`);
    }

    parts.push(`🕐 Timp: ${new Date().toLocaleString('ro-RO')}`);

    return parts.join('\n');
  };

  // Funcție pentru copierea textului în clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Micro-feedback vizual
      const notification = document.querySelector(
        '.notification-copy-feedback',
      );
      if (notification) {
        notification.textContent = '✅ Copiat!';
        setTimeout(() => {
          notification.textContent = '📋 Copiază';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback pentru browsere mai vechi
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleButtonClick = async () => {
    if (loading) return;
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    // Verifică mai întâi dacă are credite disponibile
    setNotification(null);

    try {
      const creditsCheck = await checkOfferGenerationRights();

      if (!creditsCheck.canGenerate) {
        setNotification({
          type: 'error',
          message: 'Credite insuficiente pentru generarea ofertei',
          details: `❌ Status: Nu aveți credite disponibile\n🔋 Credite abonament: ${creditsCheck.availableCredits}\n💳 Credite extra: ${creditsCheck.extraCredits}\n📊 Total disponibile: ${creditsCheck.totalCredits}\n💡 Soluție: Cumpărați credite extra sau actualizați abonamentul\n🛒 Link: /cumpara-credite`,
          copyable: true,
          errorCode: 'INSUFFICIENT_CREDITS',
        });
        return;
      }

      // Dacă are credite, continuă cu procesarea
      await processOfferGeneration();
    } catch (error) {
      console.error('Error checking credits:', error);
      setNotification({
        type: 'error',
        message: 'Eroare la verificarea creditelor',
        details: `❌ Nu s-au putut verifica creditele disponibile\n🕐 Timp: ${new Date().toLocaleString('ro-RO')}\n📋 Detalii: ${error.message || 'Eroare de comunicare cu serverul'}\n💡 Soluție: Încercați din nou sau contactați suportul`,
        copyable: true,
        errorCode: 'CREDITS_CHECK_ERROR',
      });
    }
  };

  const processOfferGeneration = async () => {
    setIsLoading(true);
    setNotification(null);

    try {
      const result = await submitOfferRequest(auctionId);

      if (result.success) {
        setNotification({
          type: 'success',
          message: 'Cererea pentru ofertă tehnică a fost trimisă cu succes!',
          details: formatSuccessDetails(result),
          copyable: true,
          requestData: result,
        });

        // Declanșează evenimentul pentru actualizarea creditelor în Header
        window.dispatchEvent(new CustomEvent('creditsUpdated'));
      } else {
        setNotification({
          type: 'error',
          message: result.message || 'A apărut o eroare la procesarea cererii.',
          details: formatErrorDetails(result),
          copyable: true,
          errorCode: result.errorCode,
        });
      }
    } catch (error) {
      console.error('Eroare la trimiterea cererii:', error);
      setNotification({
        type: 'error',
        message: 'A apărut o eroare la trimiterea cererii.',
        details: `❌ Tip eroare: Eroare de rețea sau sistem\n🕐 Timp: ${new Date().toLocaleString('ro-RO')}\n📋 Detalii: ${error.message || 'Eroare necunoscută'}\n💡 Soluție: Verificați conexiunea la internet și încercați din nou`,
        copyable: true,
        errorCode: 'NETWORK_ERROR',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Varianta de secțiune
  if (variant === 'section') {
    return (
      <>
        <section className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-200 rounded-xl shadow-lg p-4 flex flex-col justify-between gap-3 h-full">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-indigo-700 leading-tight">Oferta Tehnica</h2>
                <span className="text-[10px] text-indigo-400 font-medium">Raspuns garantat in 24 ore</span>
              </div>
            </div>
            <p className="text-gray-600 text-xs leading-snug mb-2">
              Solicita o oferta tehnica personalizata pentru aceasta procedura. Echipa noastra de specialisti va reveni cu un raspuns complet.
            </p>
            <ul className="space-y-1 mb-3">
              {['Analiza completa a cerintelor', 'Oferta tehnica detaliata', 'Consultanta specialisti', 'Livrare in max. 24 ore'].map((item, idx) => (
                <li key={idx} className="flex items-center gap-1.5 text-xs text-gray-700">
                  <svg className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <button
              onClick={(e) => { e.stopPropagation(); handleButtonClick(); }}
              disabled={isLoading}
              className={`w-full px-4 py-2.5 rounded-xl text-sm font-bold shadow-md transition ${isLoading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white hover:from-indigo-700 hover:via-purple-700 hover:to-indigo-800'}`}
            >
              {isLoading ? (
                <span className="flex items-center gap-2 justify-center">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-white rounded-full animate-spin" />
                  Se trimite...
                </span>
              ) : (
                'Genereaza Oferta Tehnica'
              )}
            </button>
          </div>
        </section>

        {/* Modal autentificare */}
        {showAuthModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={(e) => { e.stopPropagation(); setShowAuthModal(false); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => { e.stopPropagation(); setShowAuthModal(false); }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
                aria-label="Inchide dialogul"
              >x</button>
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText size={32} className="text-indigo-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Autentificare necesara</h3>
                <p className="text-gray-600 mb-6">Pentru a genera o oferta tehnica, trebuie sa te autentifici in cont.</p>
                <div className="flex flex-col gap-3">
                  <a href="/login" onClick={(e) => e.stopPropagation()} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl text-center">Autentificare</a>
                  <a href="/register" onClick={(e) => e.stopPropagation()} className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl text-center">Creaza cont nou</a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notificare */}
        {notification && (
          <div className={`fixed bottom-6 right-6 z-50 max-w-md p-4 rounded-xl border-l-4 shadow-xl ${notification.type === 'success' ? 'bg-green-50 border-green-400 text-green-800' : 'bg-red-50 border-red-400 text-red-800'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="font-semibold text-sm mb-1">{notification.message}</p>
                {notification.details && (
                  <pre className="text-xs whitespace-pre-line font-mono text-gray-700 bg-white rounded-lg p-2 border border-gray-200 mt-1">{notification.details}</pre>
                )}
                {notification.copyable && (
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => copyToClipboard(notification.details)} className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">Copiaza</button>
                    {notification.errorCode === 'INSUFFICIENT_CREDITS' && (
                      <a href="/cumpara-credite" className="px-2 py-1 text-xs font-medium rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700">Cumpara Credite</a>
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => setNotification(null)} className="text-gray-400 hover:text-gray-600 text-lg font-bold">x</button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Varianta de buton (originală)
  return (
    <div className="flex flex-col gap-4">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleButtonClick();
        }}
        disabled={isLoading}
        className={`relative inline-flex items-center gap-3 px-6 py-3
                   ${
                     isLoading
                       ? 'bg-gray-400 cursor-not-allowed'
                       : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                   }
                   text-white rounded-xl shadow-lg hover:shadow-xl
                   transition-all duration-200 ease-in-out
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
      >
        <div className="flex items-center justify-center bg-white/20 rounded-lg p-2">
          {isLoading ? (
            <div className="w-[22px] h-[22px] border-2 border-gray-300 border-t-white rounded-full animate-spin" />
          ) : (
            <FileText size={22} className="text-white" />
          )}
        </div>

        <div className="flex flex-col text-left leading-tight">
          <span className="font-semibold text-[15px]">
            {isLoading ? 'Se trimite...' : 'Generează Ofertă Tehnică'}
          </span>
          <span className="text-xs text-indigo-100">
            {isLoading ? 'Vă rugăm așteptați' : 'cu livrare în 24 ore'}
          </span>
        </div>
      </button>

      {/* Notificare elegantă pentru varianta button */}
      {notification && (
        <div
          className={`p-6 rounded-xl border-l-4 ${
            notification.type === 'success'
              ? 'bg-green-50 border-green-400 text-green-800'
              : 'bg-red-50 border-red-400 text-red-800'
          } shadow-md relative`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    notification.type === 'success'
                      ? 'bg-green-400'
                      : 'bg-red-400'
                  }`}
                >
                  <span className="text-white text-sm font-bold">
                    {notification.type === 'success' ? '✓' : '!'}
                  </span>
                </div>
                <p className="font-semibold text-base">
                  {notification.message}
                </p>
              </div>

              {notification.details && (
                <div className="ml-9">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <pre className="text-sm whitespace-pre-line font-mono text-gray-700 select-all cursor-text leading-relaxed">
                      {notification.details}
                    </pre>
                  </div>

                  {notification.copyable && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => copyToClipboard(notification.details)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          notification.type === 'success'
                            ? 'bg-green-100 hover:bg-green-200 text-green-700'
                            : 'bg-red-100 hover:bg-red-200 text-red-700'
                        }`}
                      >
                        <span className="notification-copy-feedback">
                          📋 Copiază
                        </span>
                      </button>

                      {notification.errorCode === 'INSUFFICIENT_CREDITS' && (
                        <a
                          href="/cumpara-credite"
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                        >
                          💳 Cumpără Credite
                        </a>
                      )}

                      {notification.type === 'success' &&
                        notification.requestData?.requestId && (
                          <button
                            onClick={() =>
                              copyToClipboard(
                                notification.requestData.requestId,
                              )
                            }
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                          >
                            🎫 Copiază ID Cerere
                          </button>
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => setNotification(null)}
              className="text-gray-400 hover:text-gray-600 ml-4 text-xl font-bold p-1 rounded-full hover:bg-white/50 transition-colors"
              aria-label="Închide notificarea"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Modal de autentificare */}
      {showAuthModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            e.stopPropagation();
            setShowAuthModal(false);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Buton X pentru închidere */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAuthModal(false);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>

            {/* Conținut modal */}
            <div className="text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText size={32} className="text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Autentificare necesară
                </h2>
                <p className="text-gray-600 mb-6">
                  Pentru a genera o ofertă tehnică, trebuie să te autentifici în
                  cont.
                </p>
              </div>

              {/* Butoane */}
              <div className="flex flex-col gap-3">
                <a
                  href="/login"
                  onClick={(e) => e.stopPropagation()}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Autentificare
                </a>
                <a
                  href="/register"
                  onClick={(e) => e.stopPropagation()}
                  className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Creează cont nou
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateOfferButton;
